import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Download, ArrowLeft, AlertCircle } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";

const OwnerDocuments = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || (userType !== "owner" && userType !== "admin"))) {
      navigate("/");
      return;
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  const handleDownload = (filename: string) => {
    const link = document.createElement('a');
    link.href = `/contrato/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MinhaAreaPageShell>
<div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate("/minha-area")}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Documentos</h1>
                <p className="text-muted-foreground">
                  Modelos de contratos para facilitar o processo de aluguel e troca
                </p>
              </div>
            </div>
          </div>

          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Importante:</strong> Estes são documentos modelos para ajudar e facilitar o processo para o proprietário. 
              Você pode utilizá-los como base para criar seus próprios contratos personalizados conforme necessário.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Modelo de Contrato de Aluguel */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle>Modelo de Contrato de Aluguel</CardTitle>
                    <CardDescription>
                      Contrato para aluguel de semana entre cotistas
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Este modelo de contrato pode ser utilizado quando você deseja alugar sua semana para outro cotista ou hóspede.
                </p>
                <Button
                  onClick={() => handleDownload('modelo-contrato-de-aluguel-de-semana-entre-cotistas.docx')}
                  className="w-full"
                  variant="default"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Modelo de Aluguel
                </Button>
              </CardContent>
            </Card>

            {/* Modelo de Contrato de Troca */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle>Modelo de Contrato de Troca</CardTitle>
                    <CardDescription>
                      Contrato para troca de semana entre cotistas
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Este modelo de contrato pode ser utilizado quando você deseja fazer uma troca de semana com outro proprietário.
                </p>
                <Button
                  onClick={() => handleDownload('modelo-contrato-de-troca-de-semana-entre-cotistas.docx')}
                  className="w-full"
                  variant="default"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Modelo de Troca
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
    </MinhaAreaPageShell>
  );
};

export default OwnerDocuments;

